/**
 * THE-1161: the one desktop gutter every page, carousel track and the top
 * nav should read at >=1024px, replacing each surface's own hardcoded
 * `64px` (and, where present, the `max-width: 1400px; margin: 0 auto` cap
 * that went with it).
 *
 * Flat 64px up to a 1440px-wide viewport, then grows — `(100vw - 1440px) /
 * 2` is negative below 1440 and `max()` floors it at 64 there. That is also
 * why a page no longer needs an explicit max-width + margin:auto cap to
 * stay centered at a "standard laptop" width: padding alone reaches
 * exactly 1440px of content on any wider viewport. A cap bought the same
 * centering but boxed the content in independently of the viewport, which
 * is what left a band of colour showing beside the hub's sticky header and
 * stopped every carousel short of the real screen edge (THE-1161 measured
 * this live: 20px short at 1440, 164px at 1728).
 *
 * Deliberately desktop-only. Below 1024px every surface keeps its own
 * existing padding — this value only replaces each surface's >=1024px
 * breakpoint, never its mobile/tablet one, so mobile (390/768) stays
 * byte-identical.
 */
export const PAGE_GUTTER_DESKTOP = 'max(64px, (100vw - 1440px) / 2)';

/**
 * The same value, negated — pairs with a positive gutter padding on an
 * ancestor so a bleeding child (a horizontally-scrolling carousel track)
 * can cancel that padding back out to the real screen edge.
 */
export const PAGE_GUTTER_DESKTOP_NEGATIVE = `calc(-1 * ${PAGE_GUTTER_DESKTOP})`;

/**
 * THE-1161 round 8 (board, 2026-10-27): a carousel card's desktop width,
 * scaled off the TRACK's own visible width (`100vw` minus the page gutter)
 * rather than off the raw viewport. A plain `clamp(<min>px, <vw>vw, <max>px)`
 * — every hub row's shape through round 7 — cannot hold a stable peek
 * fraction from 1024 to 1920: `vw` grows at a FLAT rate against a gutter that
 * itself grows at HALF that rate above 1568px (`PAGE_GUTTER_DESKTOP`'s own
 * kink), so the two curves drift apart and the peek swings from a sliver to
 * nearly a whole card well before 1920 (shepherd re-QA round 7 measured
 * 63-98% at 1100-1920 across every row). Dividing the ACTUAL visible width —
 * `100vw` minus the same gutter every row already pads by — tracks that kink
 * exactly instead of approximating it with one flat rate.
 *
 * `divisor` reads as "how many card-plus-gap slots the track should show" —
 * 4.3 is "4 full cards and a bit over a third of the 5th." `floorPx` only
 * binds below the >=1024px floor; each call site's choice of floor/divisor is
 * verified against the real acceptance widths in that row's own test (not
 * asserted here, since the "no accidental resonance" property depends on the
 * exact gap too), per `HubCarouselPeekGeometry.test.ts` /
 * `ComingUpStripStyleContract.test.ts`.
 */
export function trackCardWidthCss(floorPx: number, divisor: number, gapPx: number): string {
  return `max(${floorPx}px, calc((100vw - ${PAGE_GUTTER_DESKTOP}) / ${divisor} - ${gapPx}px))`;
}

/**
 * Same formula with an upper ceiling layered on — Occasions' tile column is
 * the one row the board asked to also stop growing ("coming down from 354 to
 * about 300 max"), rather than merely re-fit to the peek band like the other
 * four `trackCardWidthCss` callers.
 */
export function trackCardWidthCssCapped(
  floorPx: number,
  divisor: number,
  gapPx: number,
  capPx: number,
): string {
  return `clamp(${floorPx}px, calc((100vw - ${PAGE_GUTTER_DESKTOP}) / ${divisor} - ${gapPx}px), ${capPx}px)`;
}
